import { useDeviceType, useSwipeElement } from '@vfit/shared/hooks';
import { AppMobile, checkWindow } from '@vfit/shared/data-access';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IShowDetails, IStickyOfferHook } from './stickyOffer.model';
import { observeHeight } from './stickyOffer.utils';

export const useStickyOfferHook = ({
  slideBanner,
  getContentHeight,
  withProgressbar,
  title,
}: IStickyOfferHook) => {
  const [showDetails, _setShowDetails] = useState<IShowDetails>('');
  const [activationBoxHeight, setActivationBoxHeight] = useState(0);
  const [ctaContainerWidth, setCtaContainerWidth] = useState(0);
  const activationBoxRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const ctaContainerRef = useRef() as React.MutableRefObject<HTMLDivElement>;
  const { isDesktop } = useDeviceType();
  const [tmpSlideBanner, setTmpSlideBanner] = useState(slideBanner);
  const [isPulsing, setIsPulsing] = useState(false);
  const isFirstRender = useRef(true);

  const setShowDetails = (showDetails: IShowDetails) => _setShowDetails(showDetails);

  const observer = useRef(
    new ResizeObserver((entries) => {
      const { bottom, top } = entries[0].contentRect;
      const boxHeight = bottom + top;
      setActivationBoxHeight(boxHeight);

      setCtaContainerWidth(ctaContainerRef?.current?.clientWidth * 2 + 160);
      if (getContentHeight) {
        getContentHeight(boxHeight);
      }
    })
  );

  const stepProgress = useMemo(() => {
    if (withProgressbar?.show && withProgressbar.steps > 0) {
      return (withProgressbar.currentStep / withProgressbar.steps) * 100;
    }
    return 0;
  }, [withProgressbar]);

  useEffect(() => {
    const observerRef = observer.current;
    if (activationBoxRef && observerRef) {
      observerRef.observe(activationBoxRef.current);
    }
    return () => {
      if (observerRef) observerRef.disconnect();
    };
  }, [activationBoxRef, observer]);

  useEffect(() => {
    setTmpSlideBanner(slideBanner);
  }, [slideBanner]);

  const isApp = AppMobile.checkIsApp();

  const { onTouchStart, onTouchMove, onTouchEnd, swipeStatus } = useSwipeElement();

  useEffect(() => {
    if (isApp) {
      if (swipeStatus === 'up') setShowDetails('slide-up');
      if (swipeStatus === 'down') setShowDetails('slide-down');
    }
  }, [swipeStatus, isDesktop]);

  useEffect(() => {
    if (checkWindow() && !getContentHeight) {
      observeHeight(activationBoxRef);
    }
  }, [getContentHeight]);

  const toggleDetails = useCallback(() => {
    if (showDetails !== 'slide-up') {
      setTmpSlideBanner(undefined);
      setShowDetails('slide-up');
    } else {
      setShowDetails('slide-down');
    }
  }, [showDetails]);

  useEffect(() => {
    if (isFirstRender.current) {
      // Skip the first render
      isFirstRender.current = false;
      // return;
    } else {
      setIsPulsing(true);
    }
  }, [title]);

  useEffect(() => {
    if (isPulsing == true) {
      setTimeout(() => setIsPulsing(false), 2100); // Match the duration of the animation
    }
  }, [isPulsing]);

  return {
    activationBoxHeight,
    activationBoxRef,
    ctaContainerWidth,
    ctaContainerRef,
    tmpSlideBanner,
    stepProgress,
    showDetails,
    isApp,
    isDesktop,
    isPulsing,
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    toggleDetails,
    setShowDetails,
  };
};

